
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, Check, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useToast } from '@/hooks/use-toast';

interface ShareCompanyProfileLinkProps {
  companyName: string;
}

const ShareCompanyProfileLink: React.FC<ShareCompanyProfileLinkProps> = ({ companyName }) => {
  const [copied, setCopied] = useState(false);
  const { toast: uiToast } = useToast();
  
  // Create URL-safe version of the company name for the link
  const encodedName = encodeURIComponent(companyName.toLowerCase().replace(/\s+/g, '-'));
  const profileUrl = `${window.location.origin}/companies/${encodedName}`;
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    toast.success('Link copied to clipboard');
    
    // Reset copied state after 2 seconds
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const handleOpenProfile = () => {
    window.open(profileUrl, '_blank');
  };
  
  return (
    <Card className="mb-8 border border-redflags-light/70">
      <CardContent className="pt-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-center sm:text-left">
            <h3 className="text-lg font-medium text-redflags-dark mb-2">
              Share your RedFlags profile
            </h3>
            <p className="text-redflags-gray">
              Copy this link to showcase your company profile:
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenProfile}
              className="flex items-center gap-1"
            >
              View <ExternalLink className="h-4 w-4" />
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyLink}
              className="flex items-center gap-1"
            >
              {copied ? (
                <>
                  Copied <Check className="h-4 w-4" />
                </>
              ) : (
                <>
                  Copy Link <Copy className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ShareCompanyProfileLink;
